import { useEffect, useRef, type ReactNode } from "react";
import { creamTokens } from "../../design-system/creamDesignSystem";
import { Icon } from "../ui/Icon";

type ModalProps = {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
};

export function Modal({ open, title, children, onClose, footer }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab") return;
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>("button,[href],input,select,textarea,[tabindex]:not([tabindex='-1'])");
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", onKey);
    setTimeout(() => panelRef.current?.querySelector<HTMLElement>("button,[href],input,select,textarea,[tabindex]:not([tabindex='-1'])")?.focus(), 0);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div role="dialog" aria-modal="true" aria-label={title} onClick={event => { if (event.target === event.currentTarget) onClose(); }} style={{ position: "fixed", inset: 0, zIndex: 2400, background: "rgba(46,36,32,0.35)", display: "grid", placeItems: "center", padding: 18 }}>
      <section ref={panelRef} style={{ width: "min(560px, 100%)", maxHeight: "calc(100vh - 36px)", overflow: "auto", background: creamTokens.color.surface, borderRadius: 24, boxShadow: "0 28px 90px rgba(46,36,32,0.25)", border: `1px solid ${creamTokens.color.line}` }}>
        <header style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", padding: 18, borderBottom: `1px solid ${creamTokens.color.line}` }}>
          <h2 style={{ margin: 0, color: creamTokens.color.rose, fontSize: 20 }}>{title}</h2>
          <button type="button" aria-label="Close dialog" onClick={onClose} style={iconButton}><Icon name="x" size={16} /></button>
        </header>
        <div style={{ padding: 18 }}>{children}</div>
        {footer && <footer style={{ padding: 18, borderTop: `1px solid ${creamTokens.color.line}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>{footer}</footer>}
      </section>
    </div>
  );
}

const iconButton = {
  width: 36,
  height: 36,
  borderRadius: 12,
  border: `1px solid ${creamTokens.color.line}`,
  background: creamTokens.color.surface,
  color: creamTokens.color.chocolate,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
} as const;
